/**
 * EACH GROUP'S SHARE OF ONE MONTH'S AUDIENCE, ONE DIMENSION AT A TIME - for the Audience tab's bars
 * and its donut (the owner, 2026-10-06: "what is the problem in privacy to show it like a donut %
 * and number"; decision-log row 269).
 *
 * THE WHOLE IS THE LINES ON SCREEN: every group the platform sent, and the small groups' line. It is
 * the people the month counted IN THIS DIMENSION - a reader who gave no star sign is in no star-sign
 * line - so it is never called the month's audience. Since the owner's "Show its number" the small
 * groups' line always carries its number; a line sent without one (an older policy) is left out of
 * the whole, and `partial` says so, rather than a share being made up for it.
 */
import type { AudienceBreakdown } from '../../../features/cabinet/api';

export interface AudienceSlice {
  readonly key: string;
  readonly label: string;
  /** `null`: the small groups' line sent without a number. */
  readonly count: number | null;
  readonly merged: boolean;
}

export interface AudienceShares {
  /** The groups in the platform's order, the small groups' line last. */
  readonly slices: readonly AudienceSlice[];
  /** The people the shares are of: every line that has a number. */
  readonly total: number;
  /** The small groups' line came without a number, so the shares leave it out. */
  readonly partial: boolean;
}

/** The lines of one answer, and their whole. `mergedLabel` names the small groups' line. */
export function sharesOf(body: AudienceBreakdown, mergedLabel: string): AudienceShares {
  const slices: AudienceSlice[] = body.buckets.map((bucket) => ({
    key: bucket.bucketCode,
    label: bucket.label ?? bucket.bucketCode,
    count: bucket.readerCount,
    merged: false,
  }));
  const merged = body.suppressed;
  if (merged !== undefined) {
    slices.push({
      key: merged.bucketCode,
      label: mergedLabel,
      count: merged.readerCount ?? null,
      merged: true,
    });
  }
  const total = slices.reduce((sum, slice) => sum + (slice.count ?? 0), 0);
  return {
    slices,
    total,
    partial: merged !== undefined && (merged.readerCount ?? null) === null,
  };
}

/** "11%", "<1%" for a group too small to round to one, "" when there is no whole. */
export function shareText(count: number, total: number): string {
  if (total <= 0 || count <= 0) {
    return total > 0 ? '0%' : '';
  }
  const percent = (count / total) * 100;
  return percent < 1 ? '<1%' : `${String(Math.round(percent))}%`;
}
